import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useAuth } from 'react-oidc-context';
import { useSearchParams } from 'react-router-dom';
import { useDataProvider } from '../DataProvider';
import PageLoading from '../../components/UI/PageLoading';
import { useStore } from '../MobxProvider';
import { useUserAccess } from '../UserAccessProvider';
import { DEFAULT_LOCALE } from '../../../state/domain/Locale';

export const useMasterData = () => {
  const store = useStore();
  const storeItems = store.domain.masterData.get();
  return storeItems || {};
};

function AppDataProvider({
  children,
  metaDataUrl,
  getInjectableAdditionalMetaData = null,
}) {
  const store = useStore();
  const { user: { session_stat: sessionState = null } = {} } = useAuth() || {};
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { get, getApiUrl } = useDataProvider(provider);
  const userAcces = useUserAccess();

  const [searchParams, setSearchParams] = useSearchParams();
  const [initializing, setInitializing] = useState(true);
  const [error, setError] = useState(false);

  const masterData = useMasterData();

  const currentSession = store.domain.masterData.getSession();

  const cleanKeycloakParams = () => {
    const paramsToDelete = ['state', 'code', 'session_state'];
    let changed = false;

    paramsToDelete.forEach(param => {
      if (searchParams.has(param)) {
        searchParams.delete(param);
        changed = true;
      }
    });

    if (changed) {
      // Only update if at least one param was deleted
      setSearchParams(searchParams.toString(), { replace: true });
    }
  };

  const fetchAndSetMetaData = async () => {
    const data = await get(`${getApiUrl()}${metaDataUrl}`);
    if (data?.ok) {
      const newMasterData = data?.data;

      const languageSwitchEnabled =
        newMasterData?.applicationFeatureDemo?.localization || false;
      // If conditions are not true, supported languages are already set
      if (!languageSwitchEnabled) {
        store.domain.locale.setSupportedLocales([]);
        store.domain.locale.set(DEFAULT_LOCALE);
      } else if (
        languageSwitchEnabled &&
        Array.isArray(data?.data?.languages) &&
        data?.data?.languages?.length > 0
      ) {
        // Add Logic to set User Language
        store.domain.locale.set(store.domain.locale.get());
        store.domain.locale.setSupportedLocales(data?.data?.languages);
      } else {
        store.domain.locale.set(DEFAULT_LOCALE);
        store.domain.locale.setSupportedLocales([]);
      }

      if (typeof getInjectableAdditionalMetaData === 'function') {
        const injectableData = await getInjectableAdditionalMetaData(userAcces);
        newMasterData.frontendAppData = injectableData;
      }
      store.domain.masterData.set(newMasterData);
    } else {
      store.domain.masterData.set({});
      store.domain.locale.setSupportedLocales([]);
      store.domain.locale.set(DEFAULT_LOCALE);
      throw new Error('Config fetch failed');
    }
  };

  const fetchAppData = async (forceReFetch = false) => {
    try {
      const hasCachedMasterData = Object.entries(masterData)?.length > 0;

      if (!hasCachedMasterData || forceReFetch) {
        await fetchAndSetMetaData();
      }

      setError(false);
    } catch (e) {
      setError(true);
    }
    setInitializing(false);
  };

  useEffect(() => {
    /*
     * Refetch logic will execute if user logs in
     */
    const refetchData = !currentSession || currentSession !== sessionState;

    if (refetchData) {
      store.domain.masterData.setSession(sessionState);
    }

    fetchAppData(refetchData);
  }, []);

  useEffect(() => {
    cleanKeycloakParams();
  }, []);

  if (initializing) {
    return (
      <PageLoading>
        <FormattedMessage
          id="LOADING_MASTER_DATA"
          defaultMessage="Loading Master Data"
        />
      </PageLoading>
    );
  }

  if (error) {
    return (
      <p>
        <FormattedMessage
          id="ERROR_FETCHING_MASTER_DATA"
          defaultMessage="Error fetching master data"
        />
      </p>
    );
  }

  return children;
}

export default AppDataProvider;
